(() => {
  const ROOT_SELECTOR = '[data-faogo-giveaway]';
  const GLEAM_SCRIPT_SRC = 'https://widget.gleamjs.io/e.js';
  const PROMO_CANDIDATE_SELECTOR = [
    '.newsletter-bar',
    '.newsletter-section',
    '.klaviyo-form',
    '[class*="klaviyo-form"]',
    '[data-testid*="klaviyo"]',
    '.needsclick[class*="kl-private-reset-css"]',
  ].join(',');

  const suppressDuplicateWelcomePromo = () => {
    document.body.classList.add('faogo-giveaway-page');

    document.querySelectorAll(PROMO_CANDIDATE_SELECTOR).forEach((candidate) => {
      const copy = candidate.textContent?.replace(/\s+/g, ' ').trim() ?? '';
      if (!/20\s*%\s*off/i.test(copy)) return;

      candidate.setAttribute('data-faogo-giveaway-hidden-promo', 'true');
    });
  };

  const loadGleamScript = (root) => {
    if (root.dataset.campaignState !== 'active') return;
    if (!root.querySelector('[data-faogo-giveaway-embed] .e-widget')) return;
    if (document.querySelector(`script[src="${GLEAM_SCRIPT_SRC}"]`)) return;

    const script = document.createElement('script');
    script.src = GLEAM_SCRIPT_SRC;
    script.async = true;
    script.type = 'text/javascript';
    script.dataset.faogoGleamScript = 'true';
    document.head.appendChild(script);
  };

  const initializeGiveaway = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.giveawayInitialized === 'true') return;

    root.dataset.giveawayInitialized = 'true';
    root.classList.add('faogo-giveaway--ready');
    const heroPicture = root.querySelector('[data-faogo-hero-picture]');
    const heroImage = heroPicture?.querySelector('img');
    if (heroImage) {
      const heroBreakpoint = window.matchMedia('(max-width: 639px)');
      const updateHeroAlt = () => {
        const mobileSelected = heroBreakpoint.matches && heroPicture.querySelector('source');
        heroImage.alt = (mobileSelected ? heroPicture.dataset.mobileAlt : heroPicture.dataset.desktopAlt) || heroPicture.dataset.desktopAlt || '';
      };
      updateHeroAlt();
      heroBreakpoint.addEventListener('change', updateHeroAlt);
    }
    suppressDuplicateWelcomePromo();
    // Use Shopify's render clock plus monotonic elapsed time, not the device's
    // local date/timezone. This also closes an already-open tab at the cutoff.
    const renderedAt = Number(root.dataset.campaignNow);
    const startsAt = Number(root.dataset.campaignStart);
    const endsAt = Number(root.dataset.campaignEnd);
    const initializedAt = performance.now();
    const countdown = root.querySelector('[data-faogo-giveaway-countdown]');
    if (countdown) {
      // Use server-rendered instants, not a device's timezone or wall clock.
      // Reaching zero never authorizes opening a campaign held in Coming Soon.
      const updateCountdown = () => {
        const state = root.dataset.campaignState;
        const now = renderedAt + (performance.now() - initializedAt) / 1000;
        const deadline = state === 'coming_soon' ? startsAt : endsAt;
        if (!['coming_soon', 'active'].includes(state) || !Number.isFinite(now) || !Number.isFinite(deadline)) {
          countdown.textContent = '';
          return false;
        }
        const remaining = Math.max(0, Math.ceil(deadline - now));
        if (!remaining) {
          countdown.textContent = state === 'coming_soon'
            ? 'Scheduled opening time reached. Please refresh for availability.'
            : 'Entries are closed.';
          return false;
        }
        const days = Math.floor(remaining / 86400);
        const hours = String(Math.floor(remaining / 3600) % 24).padStart(2, '0');
        const minutes = String(Math.floor(remaining / 60) % 60).padStart(2, '0');
        const seconds = String(remaining % 60).padStart(2, '0');
        countdown.textContent = `${state === 'coming_soon' ? 'Opens in' : 'Closes in'} ${days}d ${hours}:${minutes}:${seconds}`;
        return true;
      };
      if (updateCountdown()) {
        const countdownTimer = window.setInterval(() => {
          if (!root.isConnected || !updateCountdown()) window.clearInterval(countdownTimer);
        }, 1000);
      }
    }
    const enforceEntryWindow = () => {
      if (root.dataset.campaignState !== 'active') return false;
      const now = renderedAt + (performance.now() - initializedAt) / 1000;
      if (Number.isFinite(now) && startsAt > 0 && endsAt > startsAt && now >= startsAt && now < endsAt) return true;
      root.dataset.campaignState = 'closed';
      root.querySelector('[aria-label="Giveaway entry"]')?.remove();
      root.querySelectorAll('a[href^="#faogo-giveaway-entry-"]').forEach(link => link.remove());
      const notice = root.querySelector('[data-faogo-campaign-expired]');
      if (notice) notice.hidden = false;
      return false;
    };
    if (enforceEntryWindow()) {
      loadGleamScript(root);
      const checkWindow = () => {
        if (!root.isConnected || !enforceEntryWindow()) {
          window.clearInterval(windowTimer);
          document.removeEventListener('visibilitychange', checkWindow);
          window.removeEventListener('pageshow', checkWindow);
        }
      };
      const remainingMs = (endsAt - renderedAt) * 1000;
      const windowTimer = window.setInterval(checkWindow, Math.min(1000, remainingMs));
      document.addEventListener('visibilitychange', checkWindow);
      window.addEventListener('pageshow', checkWindow);
    }

    const promoObserver = new MutationObserver(suppressDuplicateWelcomePromo);
    promoObserver.observe(document.body, { childList: true, subtree: true });

    root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a[href^="#faogo-giveaway-entry-"]');
      if (!link || !root.contains(link)) return;

      const targetId = link.getAttribute('href')?.slice(1);
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target || !root.contains(target)) return;

      window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
    });
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initializeGiveaway(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeGiveaway);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  document.addEventListener('shopify:section:unload', () => {
    window.requestAnimationFrame(() => {
      if (!document.querySelector(ROOT_SELECTOR)) document.body.classList.remove('faogo-giveaway-page');
    });
  });
})();
